import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, LogOut, Settings, Upload, Edit, Edit2, Trash2, FileText, Eye, Sparkles, ChevronRight, ChevronDown, ExternalLink, GripVertical, Bold, Italic, List, ListOrdered, Link as LinkIcon, BarChart, MessageSquare, UserPlus, Users, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from "@/contexts/ThemeContext";
import DarkModeToggle from "@/components/DarkModeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

export default function AdminDashboard({ user, logout }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [courses, setCourses] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [showFeaturedDialog, setShowFeaturedDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [sources, setSources] = useState([]); // Lessons (visible to students)
  const [availableSources, setAvailableSources] = useState([]); // Source library (not visible)
  
  // Admin Management State
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdminForm, setNewAdminForm] = useState({ email: '', name: '' });
  const [adminToDelete, setAdminToDelete] = useState(null);
  
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    passing_grade: 70,
    required_questions: 5,
    min_time_minutes: 30,
    ce_hours: 1.0,
    price: null,
    published: false,
    categories: []
  });

  const [sourceForm, setSourceForm] = useState({
    source_type: "text",
    title: "",
    content: "",
    url: "",
    file: null,
    transcript: "",
    image_file: null
  });

  const [featuredForm, setFeaturedForm] = useState({
    is_featured: false,
    featured_order: 1,
    learning_outcomes: [],
    requirements: ""
  });

  const [editingSource, setEditingSource] = useState(null);
  const [showEditSourceDialog, setShowEditSourceDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [courseIconFile, setCourseIconFile] = useState(null);
  const [instructorPhotoFile, setInstructorPhotoFile] = useState(null);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [showDeleteSourceDialog, setShowDeleteSourceDialog] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);
  const [showEditCourseDialog, setShowEditCourseDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(null);
  const [showContentPreview, setShowContentPreview] = useState(false);
  const [contentSections, setContentSections] = useState([]);
  const [expandedContentSections, setExpandedContentSections] = useState({});
  const [expandedContentModules, setExpandedContentModules] = useState({});
  const [editingOrganizeItem, setEditingOrganizeItem] = useState(null); // {type, id, title, content}

  const [assessmentQuestions, setAssessmentQuestions] = useState([{
    question: "",
    options: ["", "", "", ""],
    correct_answer: 0
  }]);

  const [surveyQuestions, setSurveyQuestions] = useState([{
    question: "",
    type: "short_answer",
    options: []
  }]);
  const [addingSource, setAddingSource] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Section/Module organization state
  const [showOrganizeDialog, setShowOrganizeDialog] = useState(false);
  const [sections, setSections] = useState([]);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [newLessonTitle, setNewLessonTitle] = useState("");
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newAudioTitle, setNewAudioTitle] = useState("");
  const [newAudioUrl, setNewAudioUrl] = useState("");
  const [newPdfTitle, setNewPdfTitle] = useState("");
  const [newPdfFile, setNewPdfFile] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCourses();
    fetchCategories();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/admin/courses`, { headers });
      setCourses(response.data);
    } catch (error) {
      toast.error("Error loading courses");
    }
  };

  // Admin Management Functions
  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers });
      setAdminUsers(response.data);
    } catch (error) {
      toast.error("Error loading admin users");
    }
  };

  const addAdmin = async () => {
    if (!newAdminForm.email || !newAdminForm.name) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      const formData = new FormData();
      formData.append('email', newAdminForm.email);
      formData.append('name', newAdminForm.name);
      const response = await axios.post(`${API}/admin/users/add`, formData, { headers });
      toast.success(response.data.message);
      setNewAdminForm({ email: '', name: '' });
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error adding admin");
    }
  };

  const removeAdmin = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success("Admin removed successfully");
      setAdminToDelete(null);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error removing admin");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories");
    }
  };

  const fetchSources = async (courseId) => {
    try {
      const response = await axios.get(`${API}/admin/courses/${courseId}/sources`, { headers });
      // Filter out source-only items - only show actual lessons
      const lessons = response.data.filter(s => s.is_source_only !== true && s.is_source_only !== 'true');
      setSources(lessons);
    } catch (error) {
      toast.error("Error loading sources");
    }
  };
  
  const fetchCourseContent = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/sources`, { headers });
      setContentSections(response.data.sections || []);
    } catch (error) {
      toast.error("Error loading course content");
      setContentSections([]);
    }
  };

  const fetchSectionsAndModules = async (courseId) => {
    try {
      const response = await axios.get(`${API}/admin/courses/${courseId}/sections`, { headers });
      const sectionsData = response.data;
      
      // Fetch modules for each section
      const sectionsWithModules = await Promise.all(
        sectionsData.map(async (section) => {
          try {
            const modulesResponse = await axios.get(`${API}/admin/sections/${section.id}/modules`, { headers });
            // Sort modules by order
            const sortedModules = modulesResponse.data.sort((a, b) => a.order - b.order);
            return { ...section, modules: sortedModules };
          } catch (error) {
            return { ...section, modules: [] };
          }
        })
      );
      
      // Sort sections by order
      const sortedSections = sectionsWithModules.sort((a, b) => a.order - b.order);
      setSections(sortedSections);
    } catch (error) {
      console.error("Error loading sections:", error);
      setSections([]);
    }
  };

  const createSection = async () => {
    if (!newSectionTitle.trim() || !selectedCourse) return;
    
    try {
      await axios.post(
        `${API}/admin/courses/${selectedCourse.id}/sections?title=${encodeURIComponent(newSectionTitle)}&order=${sections.length}`,
        {},
        { headers }
      );
      
      setNewSectionTitle("");
      await fetchSectionsAndModules(selectedCourse.id);
      toast.success("Section created");
    } catch (error) {
      toast.error("Error creating section");
    }
  };

  const createModule = async (sectionId) => {
    if (!newModuleTitle.trim()) return;
    
    try {
      const section = sections.find(s => s.id === sectionId);
      const moduleCount = section?.modules?.length || 0;
      
      await axios.post(
        `${API}/admin/sections/${sectionId}/modules?title=${encodeURIComponent(newModuleTitle)}&order=${moduleCount}`,
        {},
        { headers }
      );
      
      setNewModuleTitle("");
      setSelectedSection(null);
      await fetchSectionsAndModules(selectedCourse.id);
      toast.success("Module created");
    } catch (error) {
      toast.error("Error creating module");
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm("Delete this section? All modules and lessons will be unorganized.")) return;
    
    try {
      await axios.delete(`${API}/admin/sections/${sectionId}`, { headers });
      await fetchSectionsAndModules(selectedCourse.id);
      await fetchSources(selectedCourse.id);
      toast.success("Section deleted");
    } catch (error) {
      toast.error("Error deleting section");
    }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module? Lessons will be unorganized.")) return;
    
    try {
      await axios.delete(`${API}/admin/modules/${moduleId}`, { headers });
      await fetchSectionsAndModules(selectedCourse.id);
      await fetchSources(selectedCourse.id);
      toast.success("Module deleted");
    } catch (error) {
      toast.error("Error deleting module");
    }
  };

  const assignSourceToModule = async (sourceId, sectionId, moduleId) => {
    try {
      await axios.put(
        `${API}/admin/sources/${sourceId}/assign?section_id=${sectionId}&module_id=${moduleId}`,
        {},
        { headers }
      );
      
      await fetchSources(selectedCourse.id);
      await fetchSectionsAndModules(selectedCourse.id);
      toast.success("Lesson assigned");
    } catch (error) {
      toast.error("Error assigning lesson");
    }
  };

  const unassignSource = async (sourceId) => {
    try {
      await axios.put(
        `${API}/admin/sources/${sourceId}/assign?section_id=&module_id=`,
        {},
        { headers }
      );
      
      await fetchSources(selectedCourse.id);
      await fetchSectionsAndModules(selectedCourse.id);
      toast.success("Lesson moved to uncategorized");
    } catch (error) {
      toast.error("Error unassigning lesson");
    }
  };

  // Reorder sections
  const reorderSections = async (newSections) => {
    try {
      // Update order for all sections
      const updatePromises = newSections.map((section, index) => 
        axios.patch(
          `${API}/admin/sections/${section.id}/order`,
          { order: index },
          { headers }
        )
      );
      
      await Promise.all(updatePromises);
      // Don't set state here - already updated in handleSectionDragEnd
      toast.success("Sections reordered");
    } catch (error) {
      console.error("Error reordering sections:", error);
      toast.error("Error reordering sections");
      // On error, refetch to restore correct state
      await fetchSectionsAndModules(selectedCourse.id);
    }
  };

  // Reorder modules within a section
  const reorderModules = async (sectionId, newModules) => {
    try {
      // Update order for all modules in this section
      const updatePromises = newModules.map((module, index) => 
        axios.patch(
          `${API}/admin/modules/${module.id}/order`,
          { order: index },
          { headers }
        )
      );
      
      await Promise.all(updatePromises);
      // Don't set state here - already updated in handleModuleDragEnd
      toast.success("Modules reordered");
    } catch (error) {
      console.error("Error reordering modules:", error);
      toast.error("Error reordering modules");
      // On error, refetch to restore correct state
      await fetchSectionsAndModules(selectedCourse.id);
    }
  };

  // Create a new lesson
  const createNewLesson = async () => {
    if (!newLessonTitle.trim() || !selectedCourse) return;
    
    try {
      const nextOrder = sources.length;
      
      const formData = new FormData();
      formData.append('title', newLessonTitle); // Use title as-is, no auto-numbering
      formData.append('source_type', 'text');
      formData.append('content', ''); // Empty content initially
      formData.append('order', nextOrder);
      
      await axios.post(
        `${API}/admin/courses/${selectedCourse.id}/sources`,
        formData,
        { headers }
      );
      
      setNewLessonTitle('');
      await fetchSources(selectedCourse.id);
      toast.success('Lesson created successfully');
    } catch (error) {
      console.error('Error creating lesson:', error);
      toast.error('Error creating lesson');
    }
  };

  // Delete a lesson
  const deleteLesson = async (lessonId) => {
    try {
      await axios.delete(`${API}/admin/sources/${lessonId}`, { headers });
      await fetchSources(selectedCourse.id);
      toast.success('Lesson deleted successfully');
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error('Error deleting lesson');
    }
  };

  // Upload video source (to library, not as lesson yet)
  const uploadVideoSource = async () => {
    if (!newVideoTitle.trim() || !newVideoUrl.trim() || !selectedCourse) return;
    
    try {
      // Parse YouTube URL
      const extractYouTubeId = (url) => {
        const patterns = [
          /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
          /^([a-zA-Z0-9_-]{11})$/
        ];
        for (const pattern of patterns) {
          const match = url.match(pattern);
          if (match) return match[1];
        }
        return url;
      };
      
      const videoId = extractYouTubeId(newVideoUrl.trim());
      const videoUrl = videoId.includes('http') ? videoId : `https://www.youtube.com/watch?v=${videoId}`;
      
      const formData = new FormData();
      formData.append('title', newVideoTitle);
      formData.append('source_type', 'video');
      formData.append('content', '');
      formData.append('video_url', videoUrl);
      formData.append('is_source_only', 'true'); // Mark as source library item
      
      await axios.post(
        `${API}/admin/courses/${selectedCourse.id}/sources`,
        formData,
        { headers }
      );
      
      setNewVideoTitle('');
      setNewVideoUrl('');
      await fetchAvailableSources(selectedCourse.id);
      toast.success('Video uploaded to source library');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Error uploading video');
    }
  };

  // Upload audio source
  const uploadAudioSource = async () => {
    if (!newAudioTitle.trim() || !newAudioUrl.trim() || !selectedCourse) return;
    
    try {
      const formData = new FormData();
      formData.append('title', newAudioTitle);
      formData.append('source_type', 'audio');
      formData.append('content', '');
      formData.append('audio_url', newAudioUrl);
      formData.append('is_source_only', 'true');
      
      await axios.post(
        `${API}/admin/courses/${selectedCourse.id}/sources`,
        formData,
        { headers }
      );
      
      setNewAudioTitle('');
      setNewAudioUrl('');
      await fetchAvailableSources(selectedCourse.id);
      toast.success('Audio uploaded to source library');
    } catch (error) {
      console.error('Error uploading audio:', error);
      toast.error('Error uploading audio');
    }
  };

  // Upload PDF source
  const uploadPdfSource = async () => {
    if (!newPdfTitle.trim() || !newPdfFile || !selectedCourse) return;
    
    try {
      const formData = new FormData();
      formData.append('title', newPdfTitle);
      formData.append('source_type', 'pdf');
      formData.append('content', '');
      formData.append('file', newPdfFile);
      formData.append('is_source_only', 'true');
      
      await axios.post(
        `${API}/admin/courses/${selectedCourse.id}/sources`,
        formData,
        { headers }
      );
      
      setNewPdfTitle('');
      setNewPdfFile(null);
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
      await fetchAvailableSources(selectedCourse.id);
      toast.success('PDF uploaded to source library');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      toast.error('Error uploading PDF');
    }
  };

  // Toggle source as lesson
  const toggleSourceAsLesson = async (source, makeLesson) => {
    try {
      const formData = new FormData();
      formData.append('title', source.title);
      
      if (makeLesson) {
        // Add to course lessons
        const nextOrder = sources.length;
        formData.append('order', nextOrder);
        formData.append('is_source_only', 'false');
      } else {
        // Remove from course lessons (back to library)
        formData.append('is_source_only', 'true');
      }
      
      await axios.put(
        `${API}/admin/sources/${source.id}`,
        formData,
        { headers }
      );
      
      await fetchSources(selectedCourse.id);
      await fetchAvailableSources(selectedCourse.id);
      toast.success(makeLesson ? 'Source added to course' : 'Source removed from course');
    } catch (error) {
      console.error('Error toggling source:', error);
      toast.error('Error updating source');
    }
  };

  // Fetch available sources (library items)
  const fetchAvailableSources = async (courseId) => {
    try {
      const response = await axios.get(`${API}/admin/courses/${courseId}/sources`, { headers });
      // Filter for sources that are marked as source-only (not lessons yet)
      const sourceLibrary = response.data.filter(s => s.is_source_only === true || s.is_source_only === 'true');
      setAvailableSources(sourceLibrary);
    } catch (error) {
      console.error('Error loading available sources:', error);
    }
  };

  // Reorder lessons within a module
  const reorderLessons = async (moduleId, newLessons) => {
    try {
      // Update order for all lessons in this module
      const updatePromises = newLessons.map((lesson, index) => 
        axios.patch(
          `${API}/admin/sources/${lesson.id}/order`,
          { order: index },
          { headers }
        )
      );
      
      await Promise.all(updatePromises);
      // Don't fetch - local state already updated in handleLessonDragEnd
      toast.success("Lessons reordered");
    } catch (error) {
      console.error("Error reordering lessons:", error);
      toast.error("Error reordering lessons");
      // On error, refetch to restore correct state
      await fetchSources(selectedCourse.id);
    }
  };

  // Reorder all lessons globally
  const reorderAllLessons = async (newLessons) => {
    try {
      // Update order for all lessons
      const updatePromises = newLessons.map((lesson, index) => 
        axios.patch(
          `${API}/admin/sources/${lesson.id}/order`,
          { order: index },
          { headers }
        )
      );
      
      await Promise.all(updatePromises);
      toast.success("Lessons reordered");
    } catch (error) {
      console.error("Error reordering lessons:", error);
      toast.error("Error reordering lessons");
      // On error, refetch to restore correct state
      await fetchSources(selectedCourse.id);
    }
  };

  // Reorder courses globally
  const reorderCourses = async (newCourses) => {
    try {
      const courseIds = newCourses.map(course => course.id);
      
      await axios.patch(
        `${API}/admin/courses/reorder`,
        { course_ids: courseIds },
        { headers }
      );
      
      toast.success("Courses reordered");
    } catch (error) {
      console.error("Error reordering courses:", error);
      toast.error("Error reordering courses");
      // On error, refetch to restore correct state
      await fetchCourses();
    }
  };

  const uploadCourseIcon = async (courseId) => {
    if (!courseIconFile) return null;
    
    try {
      const formData = new FormData();
      formData.append('file', courseIconFile);
      
      const response = await axios.post(
        `${API}/admin/courses/${courseId}/icon`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      return response.data.icon_url;
    } catch (error) {
      console.error("Error uploading icon:", error);
      return null;
    }
  };

  const uploadInstructorPhoto = async (courseId) => {
    if (!instructorPhotoFile) return null;
    
    try {
      const formData = new FormData();
      formData.append('file', instructorPhotoFile);
      
      const response = await axios.post(
        `${API}/admin/courses/${courseId}/instructor-photo`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      
      return response.data.photo_url;
    } catch (error) {
      console.error("Error uploading instructor photo:", error);
      toast.error("Error uploading instructor photo");
      return null;
    }
  };

  const createCourse = async () => {
    try {
      const formData = new URLSearchParams();
      Object.keys(courseForm).forEach(key => {
        // Skip null or undefined values
        if (courseForm[key] !== null && courseForm[key] !== undefined) {
          formData.append(key, courseForm[key]);
        }
      });
      
      const response = await axios.post(`${API}/admin/courses?${formData}`, {}, { headers });
      const newCourseId = response.data.id;
      
      // Upload icon if provided
      if (courseIconFile) {
        await uploadCourseIcon(newCourseId);
      }
      
      toast.success("Course created successfully");
      setShowCreateDialog(false);
      setCourseIconFile(null);
      fetchCourses();
      setCourseForm({
        title: "",
        description: "",
        passing_grade: 70,
        required_questions: 5,
        min_time_minutes: 30,
        ce_hours: 1.0,
        price: null,
        published: false
      });
    } catch (error) {
      toast.error("Error creating course");
    }
  };

  const openEditCourse = (course) => {
    setEditingCourse({
      id: course.id,
      title: course.title,
      description: course.description,
      passing_grade: course.passing_grade,
      required_questions: course.required_questions,
      min_time_minutes: course.min_time_minutes,
      ce_hours: course.ce_hours || 1.0,
      price: course.price || null,
      published: course.published || false,
      instructor_name: course.instructor_name || "",
      instructor_bio: course.instructor_bio || "",
      subject_code: course.subject_code || "",
      educational_method: course.educational_method || ""
    });
    setShowEditCourseDialog(true);
  };

  const openDeleteDialog = (course) => {
    setCourseToDelete(course);
    setShowDeleteDialog(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;
    
    console.log(`Attempting to delete course ${courseToDelete.id}: ${courseToDelete.title}`);
    try {
      const response = await axios.delete(`${API}/admin/courses/${courseToDelete.id}`, { headers });
      console.log("Delete response:", response);
      toast.success("Course deleted successfully");
      setShowDeleteDialog(false);
      setCourseToDelete(null);
      fetchCourses();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(`Error deleting course: ${error.response?.data?.detail || error.message}`);
    }
  };

  const togglePublishCourse = async (course) => {
    try {
      const formData = new URLSearchParams();
      formData.append('title', course.title);
      formData.append('description', course.description);
      formData.append('passing_grade', course.passing_grade);
      formData.append('required_questions', course.required_questions);
      formData.append('min_time_minutes', course.min_time_minutes);
      formData.append('ce_hours', course.ce_hours || 0);
      if (course.price) formData.append('price', course.price);
      formData.append('published', !course.published);
      
      await axios.put(`${API}/admin/courses/${course.id}?${formData}`, {}, { headers });
      toast.success(course.published ? "Course unpublished" : "Course published");
      fetchCourses();
    } catch (error) {
      toast.error("Error updating course status");
    }
  };


  const openFeaturedDialog = (course) => {
    setSelectedCourse(course);
    setFeaturedForm({
      is_featured: course.is_featured || false,
      featured_order: course.featured_order || 1,
      learning_outcomes: course.learning_outcomes || [],
      requirements: course.requirements || ""
    });
    setShowFeaturedDialog(true);
  };

  const saveFeaturedSettings = async () => {
    if (!selectedCourse) return;
    
    try {
      // Update featured status
      await axios.patch(
        `${API}/admin/courses/${selectedCourse.id}/featured?is_featured=${featuredForm.is_featured}&featured_order=${featuredForm.featured_order}`,
        {},
        { headers }
      );
      
      // Update learning outcomes and requirements
      await axios.patch(
        `${API}/admin/courses/${selectedCourse.id}/details`,
        {
          learning_outcomes: featuredForm.learning_outcomes.filter(o => o.trim()),
          requirements: featuredForm.requirements
        },
        { headers }
      );
      
      toast.success(featuredForm.is_featured ? "Course marked as featured" : "Course unfeatured");
      setShowFeaturedDialog(false);
      fetchCourses();
    } catch (error) {
      toast.error("Error updating featured settings");
      console.error(error);
    }
  };

  const addLearningOutcome = () => {
    if (featuredForm.learning_outcomes.length < 5) {
      setFeaturedForm({
        ...featuredForm,
        learning_outcomes: [...featuredForm.learning_outcomes, ""]
      });
    }
  };

  const updateLearningOutcome = (index, value) => {
    const updated = [...featuredForm.learning_outcomes];
    updated[index] = value;
    setFeaturedForm({ ...featuredForm, learning_outcomes: updated });
  };

  const openCategoryDialog = (course) => {
    setSelectedCourse(course);
    setCourseForm({ ...courseForm, categories: course.categories || [] });
    setShowCategoryDialog(true);
  };

  const saveCourseCategories = async () => {
    if (!selectedCourse) return;
    
    try {
      await axios.patch(
        `${API}/admin/courses/${selectedCourse.id}/categories`,
        courseForm.categories || [],
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      
      toast.success("Categories updated");
      setShowCategoryDialog(false);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error updating categories");
      console.error(error);
    }
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Category name is required");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/categories?name=${encodeURIComponent(newCategoryName)}`,
        {},
        { headers }
      );
      
      toast.success("Category created");
      setNewCategoryName('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creating category");
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!confirm('Delete this category? It will be removed from all courses.')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, { headers });
      toast.success("Category deleted");
      fetchCategories();
      fetchCourses();
    } catch (error) {
      toast.error("Error deleting category");
    }
  };

  const toggleCourseCategory = (categoryId) => {
    const current = courseForm.categories || [];
    const updated = current.includes(categoryId)
      ? current.filter(id => id !== categoryId)
      : [...current, categoryId];
    
    setCourseForm({ ...courseForm, categories: updated });
  };

  const removeLearningOutcome = (index) => {
    const updated = featuredForm.learning_outcomes.filter((_, i) => i !== index);
    setFeaturedForm({ ...featuredForm, learning_outcomes: updated });
  };


  const updateCourse = async () => {
    if (!editingCourse) return;
    
    try {
      const formData = new URLSearchParams();
      formData.append("title", editingCourse.title);
      formData.append("description", editingCourse.description);
      formData.append("passing_grade", editingCourse.passing_grade);
      formData.append("required_questions", editingCourse.required_questions);
      formData.append("min_time_minutes", editingCourse.min_time_minutes);
      formData.append("ce_hours", editingCourse.ce_hours || 0);
      // Only append price if it's a number greater than 0 (skip for free courses)
      if (editingCourse.price && editingCourse.price > 0) {
        formData.append("price", editingCourse.price);
      }
      formData.append("published", editingCourse.published || false);
      
      // Add instructor fields (photo is uploaded separately)
      if (editingCourse.instructor_name) {
        formData.append("instructor_name", editingCourse.instructor_name);
      }
      if (editingCourse.instructor_bio) {
        formData.append("instructor_bio", editingCourse.instructor_bio);
      }
      // Add AGD PACE subject code
      if (editingCourse.subject_code !== undefined) {
        formData.append("subject_code", editingCourse.subject_code || "");
      }
      // Add educational method
      if (editingCourse.educational_method !== undefined) {
        formData.append("educational_method", editingCourse.educational_method || "");
      }
      
      await axios.put(`${API}/admin/courses/${editingCourse.id}?${formData}`, {}, { headers });
      
      // Upload new icon if provided
      if (courseIconFile) {
        await uploadCourseIcon(editingCourse.id);
      }
      
      // Upload new instructor photo if provided
      if (instructorPhotoFile) {
        await uploadInstructorPhoto(editingCourse.id);
      }
      
      toast.success("Course updated successfully");
      setShowEditCourseDialog(false);
      setEditingCourse(null);
      setCourseIconFile(null);
      setInstructorPhotoFile(null);
      fetchCourses();
    } catch (error) {
      toast.error("Error updating course");
    }
  };


  const generateCourseContent = async (course) => {
    if (!course) return;
    
    setGeneratingContent(course.id);
    
    try {
      toast.info("AI is analyzing source materials and generating course content... This may take a minute.");
      
      const response = await axios.post(
        `${API}/admin/courses/${course.id}/generate-content`,
        {},
        { headers }
      );
      
      if (response.data.success) {
        const stats = response.data.stats;
        toast.success(
          `Course content generated! Created ${stats.sections} sections, ${stats.modules} modules, ${stats.lessons} lessons, and ${stats.questions} questions.`
        );
        
        // Refresh course data
        await fetchCourses();
      }
    } catch (error) {
      console.error("Error generating content:", error);
      toast.error(error.response?.data?.detail || "Failed to generate course content. Make sure you have uploaded source materials first.");
    } finally {
      setGeneratingContent(null);
    }
  };


  const addSource = async () => {
    if (!selectedCourse) {
      toast.error("No course selected");
      return;
    }
    
    // Validation
    if (!sourceForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    if (sourceForm.source_type === "text" && !sourceForm.content.trim()) {
      toast.error("Please enter text content");
      return;
    }
    
    if ((sourceForm.source_type === "url" || sourceForm.source_type === "video") && !sourceForm.url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    
    if (sourceForm.source_type === "pdf" && !sourceForm.file) {
      toast.error("Please select a PDF file");
      return;
    }
    
    if (sourceForm.source_type === "video_upload" && !sourceForm.file) {
      toast.error("Please select a video file");
      return;
    }
    
    setAddingSource(true);
    
    try {
      // For video uploads, use Cloudinary (direct browser upload)
      if (sourceForm.source_type === "video_upload" && sourceForm.file) {
        setIsUploading(true);
        setUploadProgress(0);
        
        const file = sourceForm.file;
        console.log(`Starting Cloudinary upload: ${file.name}, ${(file.size / 1024 / 1024).toFixed(2)} MB`);
        toast.info("Uploading to Cloudinary...");
        
        try {
          // 1. Get signature from our backend
          const sigResponse = await axios.get(
            `${API}/cloudinary/signature?resource_type=video&folder=course_videos`,
            { headers }
          );
          const sig = sigResponse.data;
          console.log("Got Cloudinary signature:", sig);
          
          // 2. Upload directly to Cloudinary
          const cloudinaryForm = new FormData();
          cloudinaryForm.append("file", file);
          cloudinaryForm.append("api_key", sig.api_key);
          cloudinaryForm.append("timestamp", sig.timestamp);
          cloudinaryForm.append("signature", sig.signature);
          cloudinaryForm.append("folder", sig.folder);
          
          const cloudinaryResponse = await axios.post(
            `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`,
            cloudinaryForm,
            {
              onUploadProgress: (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(progress);
              },
              timeout: 0 // No timeout for large files
            }
          );
          
          console.log("Cloudinary upload complete:", cloudinaryResponse.data);
          const cloudinaryData = cloudinaryResponse.data;
          
          // 3. Create source record in our database
          const sourceFormData = new FormData();
          sourceFormData.append("title", sourceForm.title);
          sourceFormData.append("cloudinary_url", cloudinaryData.secure_url);
          sourceFormData.append("cloudinary_public_id", cloudinaryData.public_id);
          if (sourceForm.transcript) sourceFormData.append("transcript", sourceForm.transcript);
          
          await axios.post(
            `${API}/admin/courses/${selectedCourse.id}/cloudinary-source`,
            sourceFormData,
            { headers: { ...headers, "Content-Type": "multipart/form-data" } }
          );
          
          toast.success("Video uploaded successfully!");
          setIsUploading(false);
          setUploadProgress(0);
        } catch (err) {
          console.error("Cloudinary upload error:", err);
          throw err;
        }
      } else {
        // Regular upload for non-video sources
        const formData = new FormData();
        formData.append("source_type", sourceForm.source_type);
        formData.append("title", sourceForm.title);
        if (sourceForm.content) formData.append("content", sourceForm.content);
        if (sourceForm.url) formData.append("url", sourceForm.url);
        if (sourceForm.transcript) formData.append("transcript", sourceForm.transcript);
        if (sourceForm.file) {
          formData.append("file", sourceForm.file);
          console.log("Uploading file:", sourceForm.file.name, sourceForm.file.size, "bytes");
        }
        
        const response = await axios.post(
          `${API}/admin/courses/${selectedCourse.id}/sources`,
          formData,
          { headers: { ...headers, "Content-Type": "multipart/form-data" } }
        );
        
        console.log("Source added response:", response.data);
        toast.success("Source added successfully!");
      }
      
      fetchSources(selectedCourse.id);
      
      // Reset form
      setSourceForm({
        source_type: "text",
        title: "",
        content: "",
        url: "",
        file: null,
        transcript: ""
      });
    } catch (error) {
      console.error("Add source error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Error adding source";
      toast.error(errorMessage);
      setIsUploading(false);
      setUploadProgress(0);
    } finally {
      setAddingSource(false);
    }
  };

  const openEditSource = (source) => {
    setEditingSource(source);
    setShowEditSourceDialog(true);
  };

  const updateSource = async () => {
    if (!editingSource) return;
    
    try {
      const formData = new FormData();
      formData.append("title", editingSource.title);
      if (editingSource.content) formData.append("content", editingSource.content);
      if (editingSource.url) formData.append("url", editingSource.url);
      if (editingSource.image_file) {
        formData.append("image", editingSource.image_file);
      }
      
      await axios.put(
        `${API}/admin/sources/${editingSource.id}`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Source updated successfully");
      setShowEditSourceDialog(false);
      setEditingSource(null);
      fetchSources(selectedCourse.id);
    } catch (error) {
      toast.error("Error updating source");
    }
  };

  const confirmDeleteSource = async () => {
    if (!sourceToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/sources/${sourceToDelete.id}`, { headers });
      toast.success("Source deleted successfully");
      fetchSources(selectedCourse.id);
      setShowDeleteSourceDialog(false);
      setSourceToDelete(null);
    } catch (error) {
      toast.error("Error deleting source");
    }
  };

  const saveAssessment = async () => {
    if (!selectedCourse) return;
    
    try {
      await axios.post(
        `${API}/admin/courses/${selectedCourse.id}/assessment`,
        { questions: assessmentQuestions },
        { headers }
      );
      toast.success("Assessment saved");
      setShowAssessmentDialog(false);
    } catch (error) {
      toast.error("Error saving assessment");
    }
  };

  const saveSurvey = async () => {
    if (!selectedCourse) return;
    
    // Filter out empty questions
    const validQuestions = surveyQuestions.filter(q => {
      if (typeof q === 'string') {
        return q.trim();
      }
      return q.question && q.question.trim();
    });
    
    if (validQuestions.length === 0) {
      toast.error("Please add at least one question");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/courses/${selectedCourse.id}/survey`,
        { questions: validQuestions },
        { headers }
      );
      toast.success("Survey saved");
      setShowSurveyDialog(false);
    } catch (error) {
      toast.error("Error saving survey");
      console.error("Survey save error:", error);
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Drag must move 8px before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sortable Section Component
  const SortableSection = ({ section, sectionIndex }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: section.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <Card ref={setNodeRef} style={style} className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 flex-1">
            <button
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="w-4 h-4 text-gray-400" />
            </button>
            <h3 className="text-lg font-semibold">
              Section {sectionIndex + 1}: {section.title}
            </h3>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setEditingOrganizeItem({
                  type: 'section',
                  id: section.id,
                  title: section.title,
                  description: section.description || '',
                  video_url: section.video_url || '',
                  audio_url: section.audio_url || ''
                });
              }}
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (selectedSection === section.id) {
                  setSelectedSection(null);
                  setNewModuleTitle("");
                } else {
                  setSelectedSection(section.id);
                  setNewModuleTitle("");
                }
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Module
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => deleteSection(section.id)}
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        </div>

        {/* Add Module Form */}
        {selectedSection === section.id && (
          <div className="mb-3 flex gap-2 bg-gray-50 p-3 rounded">
            <Input
              placeholder="Module title (e.g., Getting Started)"
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && createModule(section.id)}
            />
            <Button 
              size="sm"
              onClick={() => createModule(section.id)}
              disabled={!newModuleTitle.trim()}
            >
              Create
            </Button>
            <Button 
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedSection(null);
                setNewModuleTitle("");
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Sortable Modules List */}
        {section.modules && section.modules.length > 0 && (
          <SortableContext
            items={section.modules.map(m => m.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2 ml-4">
              {section.modules.map((module, moduleIndex) => (
                <SortableModule 
                  key={module.id} 
                  module={module} 
                  moduleIndex={moduleIndex}
                  sectionId={section.id}
                />
              ))}
            </div>
          </SortableContext>
        )}

        {section.modules && section.modules.length === 0 && (
          <p className="text-sm text-gray-500 ml-4">No modules yet. Click "Add Module" to create one.</p>
        )}
      </Card>
    );
  };

  // Sortable Module Component
  const SortableModule = ({ module, moduleIndex, sectionId }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: module.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const moduleLessons = sources
      .filter(s => s.module_id === module.id)
      .sort((a, b) => a.order - b.order);

    return (
      <div ref={setNodeRef} style={style} className="border rounded p-3 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-1">
            <button
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="w-3 h-3 text-gray-400" />
            </button>
            <h4 className="font-medium">
              Module {moduleIndex + 1}: {module.title}
            </h4>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditingOrganizeItem({
                  type: 'module',
                  id: module.id,
                  title: module.title,
                  description: module.description || '',
                  video_url: module.video_url || '',
                  audio_url: module.audio_url || ''
                });
              }}
            >
              <Edit2 className="w-3 h-3 mr-1" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => deleteModule(module.id)}
            >
              <Trash2 className="w-3 h-3 text-red-600" />
            </Button>
          </div>
        </div>

        {/* Sortable Lessons List */}
        {moduleLessons.length > 0 && (
          <SortableContext
            items={moduleLessons.map(l => l.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="ml-4 space-y-1">
              {moduleLessons.map((lesson) => (
                <SortableLesson key={lesson.id} lesson={lesson} />
              ))}
            </div>
          </SortableContext>
        )}
      </div>
    );
  };

  // Sortable Lesson Component
  const SortableLesson = ({ lesson, lessonNumber }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: lesson.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div 
        ref={setNodeRef} 
        style={style}
        className="text-sm text-gray-600 flex items-center justify-between p-2 hover:bg-gray-100 rounded"
      >
        <div className="flex items-center gap-2 flex-1">
          <button
            className="cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-3 h-3 text-gray-400" />
          </button>
          <span>{lesson.title}</span>
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditingOrganizeItem({
                type: 'lesson',
                id: lesson.id,
                title: lesson.title,
                content: lesson.content || '',
                video_url: lesson.video_url || '',
                audio_url: lesson.audio_url || '',
                image_url: lesson.image_url || '',
                image_file: null
              });
            }}
            className="text-xs"
          >
            <Edit2 className="w-3 h-3 mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete "${lesson.title}"? This cannot be undone.`)) {
                deleteLesson(lesson.id);
              }
            }}
            className="text-xs text-red-600 hover:text-red-800"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Delete
          </Button>
        </div>
      </div>
    );
  };

  // Sortable Course Component for drag-and-drop reordering
  const SortableCourse = ({ course }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: course.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 1000 : 1,
    };

    return (
      <Card 
        ref={setNodeRef} 
        style={{ ...style, border: '1px solid #DADEEO' }}
        data-testid={`course-card-${course.id}`} 
        className="p-4 sm:p-6 hover:shadow-xl transition-all duration-300 bg-white flex flex-col"
      >
        <div className="flex-grow">
          {/* Drag Handle */}
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg sm:text-xl font-bold" style={{ color: '#092036' }}>{course.title}</h3>
            <button
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded flex-shrink-0 ml-2"
              {...attributes}
              {...listeners}
              title="Drag to reorder"
            >
              <GripVertical className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          <p className="mb-3 sm:mb-4 text-xs sm:text-sm line-clamp-3" style={{ color: '#70767C' }}>{course.description}</p>
          <div className="text-xs sm:text-sm mb-3 sm:mb-4 space-y-1" style={{ color: '#70767C' }}>
            <p>Passing Score: {course.passing_grade}%</p>
            <p>CE Hours: {course.ce_hours || 0}h</p>
            <p>Price: {course.price ? `$${course.price}` : 'Free'}</p>
            <p className="flex items-center gap-1">
              Status: <span className={course.published ? "text-green-600 font-semibold" : "text-orange-600 font-semibold"}>
                {course.published ? '✓ Published' : '○ Unpublished'}
              </span>
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:flex lg:flex-wrap gap-2 mt-auto">
          <Button
            data-testid={`edit-course-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => openEditCourse(course)}
            className="gap-1 text-xs w-full lg:w-auto"
          >
            <Edit className="w-3 h-3" />
            Edit
          </Button>
          <Button
            data-testid={`manage-sources-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedCourse(course);
              setSourceForm({
                source_type: "text",
                title: "",
                content: "",
                url: "",
                file: null
              });
              setShowSourceDialog(true);
              fetchSources(course.id);
              fetchAvailableSources(course.id);
            }}
            className="gap-1 text-xs w-full lg:w-auto"
          >
            <Upload className="w-3 h-3" />
            Sources
          </Button>
          <Button
            data-testid={`organize-content-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={async () => {
              setSelectedCourse(course);
              setShowOrganizeDialog(true);
              await fetchSectionsAndModules(course.id);
              await fetchSources(course.id);
              await fetchAvailableSources(course.id);
            }}
            className="gap-1 text-xs w-full lg:w-auto"
          >
            <Settings className="w-3 h-3" />
            Organize
          </Button>
          <Button
            data-testid={`generate-content-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => generateCourseContent(course)}
            disabled={generatingContent === course.id}
            className="gap-1 text-xs w-full lg:w-auto"
            style={{
              borderColor: '#7C3AED',
              color: '#7C3AED'
            }}
          >
            {generatingContent === course.id ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600"></div>
                AI...
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3" />
                AI Generate
              </>
            )}
          </Button>
          <Button
            data-testid={`view-content-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => navigate(`/admin/course/${course.id}/editor`)}
            className="gap-1 text-xs w-full lg:w-auto"
            style={{
              borderColor: '#10B981',
              color: '#10B981'
            }}
          >
            <Edit2 className="w-3 h-3" />
            Edit Content
          </Button>
          <Button
            data-testid={`manage-assessment-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={async () => {
              setSelectedCourse(course);
              try {
                const response = await axios.get(`${API}/admin/courses/${course.id}/assessment`, { headers });
                if (response.data && response.data.questions && response.data.questions.length > 0) {
                  setAssessmentQuestions(response.data.questions);
                } else {
                  setAssessmentQuestions([{
                    question: "",
                    options: ["", "", "", ""],
                    correct_answer: 0
                  }]);
                }
              } catch (error) {
                console.error("Error loading assessment:", error);
                setAssessmentQuestions([{
                  question: "",
                  options: ["", "", "", ""],
                  correct_answer: 0
                }]);
              }
              setShowAssessmentDialog(true);
            }}
            className="gap-1 text-xs w-full lg:w-auto"
          >
            <FileText className="w-3 h-3" />
            Assessment
          </Button>
          <Button
            data-testid={`manage-survey-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={async () => {
              setSelectedCourse(course);
              try {
                const response = await axios.get(`${API}/admin/courses/${course.id}/survey`, { headers });
                if (response.data && response.data.questions && response.data.questions.length > 0) {
                  setSurveyQuestions(response.data.questions);
                } else {
                  setSurveyQuestions([{ question: "", type: "short_answer", options: [] }]);
                }
              } catch (error) {
                setSurveyQuestions([{ question: "", type: "short_answer", options: [] }]);
              }
              setShowSurveyDialog(true);
            }}
            className="gap-1 text-xs w-full lg:w-auto"
          >
            <MessageSquare className="w-3 h-3" />
            Survey
          </Button>
          <Button
            data-testid={`manage-featured-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => openFeaturedDialog(course)}
            className="gap-1 text-xs w-full lg:w-auto"
            style={course.is_featured ? { borderColor: '#F59E0B', color: '#F59E0B' } : {}}
          >
            <Sparkles className="w-3 h-3" />
            {course.is_featured ? 'Featured' : 'Feature'}
          </Button>
          <Button
            data-testid={`manage-categories-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => openCategoryDialog(course)}
            className="gap-1 text-xs w-full lg:w-auto"
          >
            Categories
          </Button>
          <Button
            data-testid={`toggle-publish-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => togglePublishCourse(course)}
            className={`gap-1 text-xs w-full lg:w-auto ${course.published ? 'border-orange-500 text-orange-600' : 'border-green-500 text-green-600'}`}
          >
            <Eye className="w-3 h-3" />
            {course.published ? 'Unpublish' : 'Publish'}
          </Button>
          <Button
            data-testid={`delete-course-btn-${course.id}`}
            size="sm"
            variant="outline"
            onClick={() => openDeleteDialog(course)}
            className="gap-1 text-xs w-full lg:w-auto border-red-200 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3" />
            Delete
          </Button>
        </div>
      </Card>
    );
  };

  // Handle course drag end
  const handleCourseDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return; // Dragged outside drop zone

    if (active.id !== over.id) {
      const oldIndex = courses.findIndex(c => c.id === active.id);
      const newIndex = courses.findIndex(c => c.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return;
      
      const newCourses = arrayMove(courses, oldIndex, newIndex);
      
      // Update local state immediately
      setCourses(newCourses);
      
      // Then persist to backend
      reorderCourses(newCourses);
    }
  };

  // Drag end handlers
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return; // Dragged outside drop zone

    if (active.id !== over.id) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return;
      
      const newSections = arrayMove(sections, oldIndex, newIndex);
      
      // Update local state immediately
      setSections(newSections);
      
      // Then persist to backend
      reorderSections(newSections);
    }
  };

  const handleModuleDragEnd = (event, sectionId) => {
    const { active, over } = event;

    if (!over) return; // Dragged outside drop zone

    const section = sections.find(s => s.id === sectionId);
    if (!section || !section.modules) return;

    if (active.id !== over.id) {
      const oldIndex = section.modules.findIndex(m => m.id === active.id);
      const newIndex = section.modules.findIndex(m => m.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return;
      
      const newModules = arrayMove(section.modules, oldIndex, newIndex);
      
      // Update local state immediately
      setSections(sections.map(s => {
        if (s.id === sectionId) {
          return { ...s, modules: newModules };
        }
        return s;
      }));
      
      // Then persist to backend
      reorderModules(sectionId, newModules);
    }
  };

  const handleLessonDragEnd = (event, moduleId) => {
    const { active, over } = event;

    if (!over) return; // Dragged outside drop zone

    const moduleLessons = sources
      .filter(s => s.module_id === moduleId)
      .sort((a, b) => a.order - b.order);

    if (active.id !== over.id) {
      const oldIndex = moduleLessons.findIndex(l => l.id === active.id);
      const newIndex = moduleLessons.findIndex(l => l.id === over.id);
      
      if (oldIndex === -1 || newIndex === -1) return; // Item not found
      
      const newLessons = arrayMove(moduleLessons, oldIndex, newIndex);
      
      // Update local state immediately for visual feedback
      const updatedSources = sources.map(source => {
        const lessonIndex = newLessons.findIndex(l => l.id === source.id);
        if (lessonIndex !== -1) {
          return { ...source, order: lessonIndex };
        }
        return source;
      }).sort((a, b) => {
        // Sort by order within the same module
        if (a.module_id === b.module_id) {
          return a.order - b.order;
        }
        return 0;
      });
      setSources(updatedSources);
      
      // Then persist to backend
      reorderLessons(moduleId, newLessons);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: isDarkMode ? '#092036' : 'linear-gradient(135deg, #F4F4F4 0%, #DADEEO 100%)' }}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDarkMode ? '' : 'bg-white/90 backdrop-blur-md border-b'}`} style={{ backgroundColor: isDarkMode ? '#092036' : undefined, borderColor: isDarkMode ? 'transparent' : '#DADEEO' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 cursor-pointer" onClick={() => navigate("/")}>
              <img src={LOGO_URL} alt="Oral Surgery Success" className="h-8 w-8 sm:h-10 sm:w-10 object-contain flex-shrink-0" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl font-bold truncate" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>Admin Dashboard</h1>
                <p className="text-xs sm:text-sm truncate" style={{ color: isDarkMode ? '#e2e8f0' : '#70767C' }}>{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto items-center">
              <DarkModeToggle />
              <Button 
                data-testid="view-as-student-btn"
                onClick={() => navigate("/dashboard")}
                variant="outline"
                size="sm"
                className="gap-1 sm:gap-2 flex-1 sm:flex-initial"
                style={{ borderColor: isDarkMode ? '#60a5fa' : '#102862', color: isDarkMode ? '#60a5fa' : '#102862' }}
              >
                <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">View as Student</span>
                <span className="sm:hidden">Student</span>
              </Button>
              <Button 
                data-testid="admin-logout-btn"
                onClick={logout}
                variant="outline"
                size="sm"
                className="gap-1 sm:gap-2"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>Courses</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              onClick={() => navigate("/admin/curbside-consult")}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
            >
              <MessageSquare className="w-4 h-4" />
              Curbside Consult
            </Button>
            <Button
              onClick={() => navigate("/admin/practice-resources")}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
            >
              <FileText className="w-4 h-4" />
              Practice Resources
            </Button>
            <Button
              onClick={() => navigate("/admin/agd-report")}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
              data-testid="admin-agd-report-nav-btn"
            >
              <FileText className="w-4 h-4" />
              AGD Annual Report
            </Button>
            <Button
              onClick={() => navigate("/admin/students")}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
              data-testid="admin-students-nav-btn"
            >
              <Mail className="w-4 h-4" />
              Students & Email
            </Button>
            <Button
              onClick={() => navigate("/admin/student-results")}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
            >
              <BarChart className="w-4 h-4" />
              Student Results
            </Button>
            <Button
              onClick={() => navigate("/admin/landing-page")}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
            >
              <Edit className="w-4 h-4" />
              Edit Landing Page
            </Button>
            <Button
              onClick={() => {
                fetchAdminUsers();
                setShowAdminDialog(true);
              }}
              variant="outline"
              size="sm"
              className="gap-2 w-full sm:w-auto"
            >
              <Users className="w-4 h-4" />
              Manage Admins
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="create-course-btn"
                  size="sm"
                  className="gap-2 text-white border-0 w-full sm:w-auto"
                  style={{ background: '#102862' }}
                >
                  <Plus className="w-4 h-4" />
                  Create Course
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl light-form-card">
              <DialogHeader>
                <DialogTitle>Create New Course</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    data-testid="course-title-input"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({...courseForm, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    data-testid="course-description-input"
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Passing Grade (%)</Label>
                    <Input
                      data-testid="course-passing-grade-input"
                      type="number"
                      value={courseForm.passing_grade}
                      onChange={(e) => setCourseForm({...courseForm, passing_grade: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Required Questions</Label>
                    <Input
                      data-testid="course-required-questions-input"
                      type="number"
                      value={courseForm.required_questions}
                      onChange={(e) => setCourseForm({...courseForm, required_questions: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Min Time (minutes)</Label>
                    <Input
                      data-testid="course-min-time-input"
                      type="number"
                      value={courseForm.min_time_minutes}
                      onChange={(e) => setCourseForm({...courseForm, min_time_minutes: parseInt(e.target.value)})}
                      placeholder="e.g., 60 for 1 hour"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>CE Hours</Label>
                    <Input
                      data-testid="course-ce-hours-input"
                      type="number"
                      step="0.5"
                      value={courseForm.ce_hours}
                      onChange={(e) => setCourseForm({...courseForm, ce_hours: parseFloat(e.target.value)})}
                      placeholder="e.g., 1.5"
                    />
                  </div>
                  <div>
                    <Label>Price</Label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={courseForm.price === null}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCourseForm({...courseForm, price: null});
                            } else {
                              setCourseForm({...courseForm, price: ""});
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-medium">Free Course</span>
                      </label>
                      {courseForm.price !== null && (
                        <Input
                          data-testid="course-price-input"
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={courseForm.price || ""}
                          onChange={(e) => setCourseForm({...courseForm, price: e.target.value ? parseFloat(e.target.value) : ""})}
                          placeholder="Enter price"
                        />
                      )}
                      {courseForm.price === null && (
                        <p className="text-sm text-gray-500">This course will be free for all students</p>
                      )}
                      {courseForm.price && courseForm.price > 0 && (
                        <p className="text-sm text-gray-500">Price: ${courseForm.price.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={courseForm.published}
                        onChange={(e) => setCourseForm({...courseForm, published: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Published</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <Label>Course Icon/Thumbnail (optional)</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCourseIconFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-slate-500 mt-1">Recommended: 800x400px, max 2MB</p>
                  {courseIconFile && (
                    <p className="text-xs text-green-600 mt-1">✓ {courseIconFile.name} selected</p>
                  )}
                </div>
                
                <Button 
                  data-testid="save-course-btn"
                  onClick={createCourse}
                  className="w-full text-white border-0"
                  style={{ background: '#102862' }}
                >
                  Create Course
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Drag-and-drop Course Cards */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleCourseDragEnd}
        >
          <SortableContext
            items={courses.map(c => c.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
              {courses.map(course => (
                <SortableCourse key={course.id} course={course} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Drag hint */}
        {courses.length > 1 && (
          <p className="text-xs text-center mt-4" style={{ color: isDarkMode ? '#9ca3af' : '#70767C' }}>
            Drag courses by the grip icon to reorder them
          </p>
        )}
      </main>

      {/* Source Dialog */}
      <Dialog open={showSourceDialog} onOpenChange={(open) => {
        setShowSourceDialog(open);
        if (!open) {
          // Reset form when closing
          setSourceForm({
            source_type: "text",
            title: "",
            content: "",
            url: "",
            file: null
          });
        }
      }}>
        <DialogContent className="max-w-2xl light-form-card">
          <DialogHeader>
            <DialogTitle>Manage Sources - {selectedCourse?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Source Type</Label>
              <select
                data-testid="source-type-select"
                className="w-full border rounded-md p-2"
                value={sourceForm.source_type}
                onChange={(e) => setSourceForm({...sourceForm, source_type: e.target.value, file: null, url: ''})}
              >
                <option value="text">Text Content</option>
                <option value="pdf">PDF Document</option>
                <option value="url">Web URL / Article</option>
                <option value="video">Video URL (YouTube/Vimeo)</option>
                <option value="video_upload">Video Upload (from computer)</option>
              </select>
            </div>
            <div>
              <Label>Title</Label>
              <Input
                data-testid="source-title-input"
                value={sourceForm.title}
                onChange={(e) => setSourceForm({...sourceForm, title: e.target.value})}
              />
            </div>
            {sourceForm.source_type === "text" && (
              <div>
                <Label>Content</Label>
                <Textarea
                  data-testid="source-content-input"
                  value={sourceForm.content}
                  onChange={(e) => setSourceForm({...sourceForm, content: e.target.value})}
                  rows={5}
                />
              </div>
            )}
            {(sourceForm.source_type === "url" || sourceForm.source_type === "video") && (
              <div>
                <Label>{sourceForm.source_type === "video" ? "Video URL (YouTube, Vimeo, etc.)" : "Web URL"}</Label>
                <Input
                  data-testid="source-url-input"
                  value={sourceForm.url}
                  onChange={(e) => setSourceForm({...sourceForm, url: e.target.value})}
                  placeholder={sourceForm.source_type === "video" ? "https://www.youtube.com/watch?v=..." : "https://example.com/article"}
                />
              </div>
            )}
            {sourceForm.source_type === "video" && (
              <div>
                <Label>Video Transcript (Optional)</Label>
                <Textarea
                  value={sourceForm.transcript || ""}
                  onChange={(e) => setSourceForm({...sourceForm, transcript: e.target.value})}
                  placeholder="Enter the video transcript here..."
                  rows={5}
                />
              </div>
            )}
            {sourceForm.source_type === "video_upload" && (
              <div className="space-y-4">
                <div>
                  <Label>Upload Video File</Label>
                  <Input
                    data-testid="source-video-file-input"
                    type="file"
                    accept="video/*,.mp4,.mov,.avi,.webm,.mkv"
                    disabled={isUploading}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Check file size (max 2GB)
                        if (file.size > 2 * 1024 * 1024 * 1024) {
                          toast.error("Video file must be less than 2GB");
                          return;
                        }
                        setSourceForm({...sourceForm, file});
                        setUploadProgress(0);
                        console.log("Video file selected:", file.name, (file.size / 1024 / 1024).toFixed(2), "MB");
                      }
                    }}
                  />
                  {sourceForm.file && !isUploading && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ {sourceForm.file.name} ({(sourceForm.file.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                  {isUploading && (
                    <div className="mt-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">Uploading... Please don't close this window</span>
                        <span className="font-bold">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div 
                          className="h-4 rounded-full transition-all duration-300 flex items-center justify-center"
                          style={{ 
                            width: `${Math.max(uploadProgress, 5)}%`,
                            background: 'linear-gradient(90deg, #FF6B35, #102862)'
                          }}
                        >
                          {uploadProgress > 10 && (
                            <span className="text-xs text-white font-medium">{uploadProgress}%</span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {sourceForm.file && (
                          <>
                            {((sourceForm.file.size * uploadProgress / 100) / 1024 / 1024).toFixed(1)} MB / {(sourceForm.file.size / 1024 / 1024).toFixed(1)} MB
                            <span className="ml-2">• Chunks with retry enabled</span>
                          </>
                        )}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Supported: MP4, MOV, AVI, WebM, MKV (max 2GB) • Uploads via Cloudinary</p>
                </div>
                <div>
                  <Label>Video Transcript (Optional)</Label>
                  <Textarea
                    value={sourceForm.transcript || ""}
                    onChange={(e) => setSourceForm({...sourceForm, transcript: e.target.value})}
                    placeholder="Enter the video transcript here for searchability..."
                    rows={3}
                    disabled={isUploading}
                  />
                </div>
              </div>
            )}
            {sourceForm.source_type === "pdf" && (
              <div>
                <Label>Upload PDF</Label>
                <Input
                  data-testid="source-file-input"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.type !== 'application/pdf') {
                        toast.error("Please select a PDF file");
                        return;
                      }
                      setSourceForm({...sourceForm, file});
                      console.log("PDF file selected:", file.name);
                    }
                  }}
                />
                {sourceForm.file && (
                  <p className="text-sm text-green-600 mt-2">✓ {sourceForm.file.name} selected</p>
                )}
              </div>
            )}
            <Button 
              data-testid="add-source-btn"
              onClick={addSource}
              disabled={addingSource || isUploading}
              className="w-full text-white border-0"
              style={{ background: isUploading ? '#70767C' : '#102862' }}
            >
              {isUploading ? `Uploading... ${uploadProgress}%` : addingSource ? "Adding..." : "Add Source"}
            </Button>
            
            <div className="mt-6">
              <h4 className="font-semibold mb-3" style={{ color: '#092036' }}>
                Existing Sources ({sources.length + availableSources.length})
              </h4>
              <p className="text-xs text-gray-600 mb-3">
                Toggle "Show in Course" to make sources visible to students as lessons
              </p>
              {sources.length === 0 && availableSources.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: '#70767C' }}>No sources added yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {/* Show all sources (both lessons and library items) */}
                  {[...sources, ...availableSources].map(source => {
                    const isLesson = source.is_source_only !== true && source.is_source_only !== 'true';
                    return (
                      <div key={source.id} data-testid={`source-item-${source.id}`} className="p-3 rounded-lg border hover:border-blue-300 transition-colors overflow-hidden" style={{ background: '#F4F4F4', borderColor: '#DADEEO' }}>
                        <div className="flex justify-between items-start gap-3 min-w-0">
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-medium break-words" style={{ color: '#092036', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{source.title}</p>
                              {isLesson && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded whitespace-nowrap">In Course</span>
                              )}
                            </div>
                            <p className="text-xs mt-1">
                              <span className="inline-block px-2 py-1 rounded" style={{ background: '#DADEEO', color: '#092036' }}>{source.source_type}</span>
                            </p>
                          </div>
                          <div className="flex flex-col gap-2 shrink-0 items-end">
                            <label className="flex items-center gap-1 cursor-pointer text-xs">
                              <input
                                type="checkbox"
                                checked={isLesson}
                                onChange={(e) => toggleSourceAsLesson(source, e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="whitespace-nowrap">Show in Course</span>
                            </label>
                            <div className="flex gap-1">
                              <Button
                                data-testid={`edit-source-btn-${source.id}`}
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditSource(source)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="w-4 h-4" style={{ color: '#102862' }} />
                              </Button>
                              <Button
                                data-testid={`delete-source-btn-${source.id}`}
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSourceToDelete(source);
                                  setShowDeleteSourceDialog(true);
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="w-4 h-4" style={{ color: '#9E3639' }} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Source Dialog */}
      <Dialog open={showEditSourceDialog} onOpenChange={setShowEditSourceDialog}>
        <DialogContent className="max-w-2xl light-form-card">
          <DialogHeader>
            <DialogTitle>Edit Source</DialogTitle>
          </DialogHeader>
          {editingSource && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Title</Label>
                <Input
                  data-testid="edit-source-title-input"
                  value={editingSource.title}
                  onChange={(e) => setEditingSource({...editingSource, title: e.target.value})}
                />
              </div>
              {editingSource.source_type === "text" && (
                <div>
                  <Label>Content</Label>
                  <Textarea
                    data-testid="edit-source-content-input"
                    value={editingSource.content || ""}
                    onChange={(e) => setEditingSource({...editingSource, content: e.target.value})}
                    rows={8}
                  />
                </div>
              )}
              {editingSource.source_type === "url" && (
                <div>
                  <Label>URL</Label>
                  <Input
                    data-testid="edit-source-url-input"
                    value={editingSource.url || ""}
                    onChange={(e) => setEditingSource({...editingSource, url: e.target.value})}
                  />
                </div>
              )}
              {editingSource.source_type === "pdf" && (
                <div className="p-4 rounded-lg" style={{ background: '#FEF3C7', borderColor: '#F59E0B', border: '1px solid' }}>
                  <p className="text-sm" style={{ color: '#92400E' }}>
                    Note: You can only edit the title for PDF sources. To change the PDF content, delete this source and upload a new one.
                  </p>
                </div>
              )}
              
              {/* Lesson Image Upload */}
              <div>
                <Label>Lesson Image/Slide (Optional)</Label>
                <p className="text-xs text-gray-500 mb-2">Upload a photo or slide to display at the top of this lesson</p>
                {editingSource.image_url && (
                  <div className="mb-2">
                    <img 
                      src={`${API.replace('/api', '')}${editingSource.image_url}`}
                      alt="Lesson preview" 
                      className="max-w-xs rounded border"
                      style={{ maxHeight: '150px' }}
                    />
                    <p className="text-xs text-gray-500 mt-1">Current image - upload a new one to replace it</p>
                  </div>
                )}
                {editingSource.image_file && (
                  <div className="mb-2 p-2 bg-green-50 rounded border border-green-200">
                    <p className="text-xs text-green-700 font-medium">✓ New image selected: {editingSource.image_file.name}</p>
                    <p className="text-xs text-green-600">This will replace the current image when you save</p>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setEditingSource({...editingSource, image_file: file});
                    }
                  }}
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  data-testid="cancel-edit-source-btn"
                  variant="outline"
                  onClick={() => {
                    setShowEditSourceDialog(false);
                    setEditingSource(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  data-testid="save-edit-source-btn"
                  onClick={updateSource}
                  className="flex-1 text-white border-0"
                  style={{ background: '#102862' }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assessment Dialog */}
      <Dialog open={showAssessmentDialog} onOpenChange={setShowAssessmentDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle className="text-xl">Manage Assessment - {selectedCourse?.title}</DialogTitle>
            <p className="text-sm text-slate-600 mt-2">
              Create custom assessment questions. If no custom questions are set, students will receive AI-generated questions based on their learning.
            </p>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {assessmentQuestions.map((q, qIdx) => (
              <Card key={qIdx} className="p-4 border-2 border-slate-200 space-y-3">
                <div className="flex justify-between items-start gap-2">
                  <span className="text-sm font-semibold text-slate-700">Question {qIdx + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const updated = assessmentQuestions.filter((_, i) => i !== qIdx);
                      setAssessmentQuestions(updated.length > 0 ? updated : [{
                        question: "",
                        options: ["", "", "", ""],
                        correct_answer: 0
                      }]);
                    }}
                    className="h-8 w-8 p-0 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
                <Textarea
                  data-testid={`assessment-question-${qIdx}`}
                  placeholder="Enter your question here..."
                  value={q.question}
                  onChange={(e) => {
                    const updated = [...assessmentQuestions];
                    updated[qIdx].question = e.target.value;
                    setAssessmentQuestions(updated);
                  }}
                  className="min-h-[80px]"
                />
                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">Answer Options (select correct answer)</Label>
                  {q.options.map((opt, oIdx) => (
                    <div key={oIdx} className="flex gap-2 items-center">
                      <span className="text-sm font-medium text-slate-600 w-6">{String.fromCharCode(65 + oIdx)}.</span>
                      <Input
                        data-testid={`assessment-option-${qIdx}-${oIdx}`}
                        placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                        value={opt}
                        onChange={(e) => {
                          const updated = [...assessmentQuestions];
                          updated[qIdx].options[oIdx] = e.target.value;
                          setAssessmentQuestions(updated);
                        }}
                        className="flex-1"
                      />
                      <input
                        type="radio"
                        name={`correct-${qIdx}`}
                        checked={q.correct_answer === oIdx}
                        onChange={() => {
                          const updated = [...assessmentQuestions];
                          updated[qIdx].correct_answer = oIdx;
                          setAssessmentQuestions(updated);
                        }}
                        className="w-5 h-5 cursor-pointer"
                        title="Mark as correct answer"
                      />
                    </div>
                  ))}
                </div>
              </Card>
            ))}
            <div className="flex gap-3">
              <Button
                data-testid="add-assessment-question-btn"
                variant="outline"
                onClick={() => setAssessmentQuestions([...assessmentQuestions, {
                  question: "",
                  options: ["", "", "", ""],
                  correct_answer: 0
                }])}
                className="flex-1"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
              <Button 
                data-testid="save-assessment-btn"
                onClick={saveAssessment}
                className="flex-1 text-white border-0"
                style={{ background: '#102862' }}
              >
                Save Custom Assessment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Survey Dialog */}
      <Dialog open={showSurveyDialog} onOpenChange={setShowSurveyDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle className="text-xl">Manage Survey - {selectedCourse?.title}</DialogTitle>
            <p className="text-sm text-slate-600 mt-2">
              Create custom course survey questions. Students must complete this survey after passing the assessment to receive their certificate.
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {surveyQuestions.map((q, idx) => (
              <Card key={idx} className="p-4 border-2 border-slate-200 space-y-3">
                <div className="flex justify-between items-start gap-2">
                  <span className="text-sm font-semibold text-slate-700">Question {idx + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const updated = surveyQuestions.filter((_, i) => i !== idx);
                      setSurveyQuestions(updated.length > 0 ? updated : [{ question: "", type: "short_answer", options: [] }]);
                    }}
                    className="h-8 w-8 p-0 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
                
                {/* Question Type Selector */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1">Question Type</Label>
                  <select
                    value={q.type || "short_answer"}
                    onChange={(e) => {
                      const updated = [...surveyQuestions];
                      const currentQuestion = typeof updated[idx] === 'string' ? updated[idx] : (updated[idx].question || '');
                      updated[idx] = {
                        question: currentQuestion,
                        type: e.target.value,
                        options: e.target.value === "multiple_choice" ? ["", "", "", ""] : []
                      };
                      setSurveyQuestions(updated);
                    }}
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    <option value="short_answer">Short Answer</option>
                    <option value="multiple_choice">Multiple Choice</option>
                  </select>
                </div>
                
                {/* Question Text */}
                <Textarea
                  data-testid={`survey-question-${idx}`}
                  placeholder={`Enter survey question ${idx + 1}...`}
                  value={typeof q === 'string' ? q : (q.question || '')}
                  onChange={(e) => {
                    const updated = [...surveyQuestions];
                    if (typeof updated[idx] === 'string') {
                      updated[idx] = { question: e.target.value, type: "short_answer", options: [] };
                    } else {
                      updated[idx] = { ...updated[idx], question: e.target.value };
                    }
                    setSurveyQuestions(updated);
                  }}
                  className="min-h-[60px]"
                />
                
                {/* Multiple Choice Options */}
                {(q.type === "multiple_choice") && (
                  <div className="space-y-2 pl-4 border-l-2 border-slate-300">
                    <Label className="text-xs text-slate-600">Answer Options</Label>
                    {(q.options || []).map((opt, optIdx) => (
                      <div key={optIdx} className="flex gap-2 items-center">
                        <span className="text-sm font-medium text-slate-600 w-6">{String.fromCharCode(65 + optIdx)}.</span>
                        <Input
                          placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                          value={opt}
                          onChange={(e) => {
                            const updated = [...surveyQuestions];
                            if (!updated[idx].options) updated[idx].options = [];
                            updated[idx].options[optIdx] = e.target.value;
                            setSurveyQuestions(updated);
                          }}
                          className="flex-1"
                        />
                        {optIdx >= 4 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = [...surveyQuestions];
                              updated[idx].options.splice(optIdx, 1);
                              setSurveyQuestions(updated);
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {q.options && q.options.length < 10 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const updated = [...surveyQuestions];
                          if (!updated[idx].options) updated[idx].options = [];
                          updated[idx].options.push("");
                          setSurveyQuestions(updated);
                        }}
                        className="text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Option
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))}
            <div className="flex gap-3">
              <Button
                data-testid="add-survey-question-btn"
                variant="outline"
                onClick={() => setSurveyQuestions([...surveyQuestions, { question: "", type: "short_answer", options: [] }])}
                className="flex-1"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
              <Button 
                data-testid="save-survey-btn"
                onClick={saveSurvey}
                className="flex-1 text-white border-0"
                style={{ background: '#102862' }}
              >
                Save Survey
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Course Dialog */}
      <Dialog open={showEditCourseDialog} onOpenChange={setShowEditCourseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle>Edit Course</DialogTitle>
          </DialogHeader>
          {editingCourse && (
            <div className="space-y-4 mt-4 pb-6">
              <div>
                <Label>Course Title</Label>
                <Input
                  data-testid="edit-course-title-input"
                  value={editingCourse.title}
                  onChange={(e) => setEditingCourse({...editingCourse, title: e.target.value})}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  data-testid="edit-course-description-input"
                  value={editingCourse.description}
                  onChange={(e) => setEditingCourse({...editingCourse, description: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Passing Grade (%)</Label>
                  <Input
                    data-testid="edit-course-passing-grade-input"
                    type="number"
                    value={editingCourse.passing_grade}
                    onChange={(e) => setEditingCourse({...editingCourse, passing_grade: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Required Questions</Label>
                  <Input
                    data-testid="edit-course-required-questions-input"
                    type="number"
                    value={editingCourse.required_questions}
                    onChange={(e) => setEditingCourse({...editingCourse, required_questions: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Min Time (minutes)</Label>
                  <Input
                    data-testid="edit-course-min-time-input"
                    type="number"
                    value={editingCourse.min_time_minutes}
                    onChange={(e) => setEditingCourse({...editingCourse, min_time_minutes: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CE Hours</Label>
                  <Input
                    data-testid="edit-course-ce-hours-input"
                    type="number"
                    step="0.5"
                    value={editingCourse.ce_hours || 0}
                    onChange={(e) => setEditingCourse({...editingCourse, ce_hours: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>AGD PACE Subject Code</Label>
                  <Input
                    data-testid="edit-course-subject-code-input"
                    type="text"
                    value={editingCourse.subject_code || ""}
                    onChange={(e) => setEditingCourse({...editingCourse, subject_code: e.target.value})}
                    placeholder="e.g., 750"
                  />
                  <p className="text-xs text-slate-500 mt-1">AGD PACE subject code for certificate</p>
                </div>
                <div>
                  <Label>Educational Method</Label>
                  <select
                    data-testid="edit-course-educational-method-select"
                    value={editingCourse.educational_method || ""}
                    onChange={(e) => setEditingCourse({...editingCourse, educational_method: e.target.value})}
                    className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select educational method...</option>
                    <option value="Lecture">Lecture</option>
                    <option value="Participation (Hands-on)">Participation (Hands-on)</option>
                    <option value="Self-instruction course">Self-instruction course</option>
                    <option value="Electronically mediated course">Electronically mediated course</option>
                    <option value="Protocol courses">Protocol courses</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Select the educational method for this course</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingCourse.price === null}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingCourse({...editingCourse, price: null});
                          } else {
                            setEditingCourse({...editingCourse, price: ""});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Free Course</span>
                    </label>
                    {editingCourse.price !== null && (
                      <Input
                        data-testid="edit-course-price-input"
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={editingCourse.price || ""}
                        onChange={(e) => setEditingCourse({...editingCourse, price: e.target.value ? parseFloat(e.target.value) : ""})}
                        placeholder="Enter price"
                      />
                    )}
                    {editingCourse.price === null && (
                      <p className="text-sm text-gray-500">This course is free</p>
                    )}
                    {editingCourse.price && editingCourse.price > 0 && (
                      <p className="text-sm text-gray-500">Price: ${editingCourse.price.toFixed(2)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingCourse.published || false}
                      onChange={(e) => setEditingCourse({...editingCourse, published: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Published</span>
                  </label>
                </div>
              </div>
              
              <div>
                <Label>Course Icon/Thumbnail</Label>
                {editingCourse.icon_url && (
                  <div className="mb-2 p-3 border rounded-lg bg-slate-50">
                    <img src={`${API}${editingCourse.icon_url}`} alt="Current icon" className="h-20 object-contain mb-2" />
                    <p className="text-xs text-slate-600">Current icon</p>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCourseIconFile(e.target.files[0])}
                  className="cursor-pointer"
                />
                <p className="text-xs text-slate-500 mt-1">Upload new icon (800x400px recommended, max 2MB)</p>
                {courseIconFile && (
                  <p className="text-xs text-green-600 mt-1">✓ {courseIconFile.name} selected - will be uploaded on save</p>
                )}
              </div>

              {/* Instructor Information Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Instructor Information</h3>
                
                <div>
                  <Label>Instructor Name</Label>
                  <Input
                    value={editingCourse.instructor_name || ""}
                    onChange={(e) => setEditingCourse({...editingCourse, instructor_name: e.target.value})}
                    placeholder="Dr. John Smith"
                  />
                </div>

                <div className="mt-3">
                  <Label>Instructor Photo</Label>
                  {editingCourse.instructor_photo_url && (
                    <div className="mb-2 p-3 border rounded-lg bg-slate-50">
                      <img 
                        src={`${API}${editingCourse.instructor_photo_url}`} 
                        alt="Current instructor photo" 
                        className="h-32 w-32 object-cover rounded-full mx-auto mb-2" 
                      />
                      <p className="text-xs text-slate-600 text-center">Current photo</p>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={(e) => setInstructorPhotoFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-slate-500 mt-1">Upload JPG or PNG (square photo recommended, max 2MB)</p>
                  {instructorPhotoFile && (
                    <p className="text-xs text-green-600 mt-1">✓ {instructorPhotoFile.name} selected - will be uploaded on save</p>
                  )}
                </div>

                <div className="mt-3">
                  <Label>Instructor Bio</Label>
                  <Textarea
                    value={editingCourse.instructor_bio || ""}
                    onChange={(e) => setEditingCourse({...editingCourse, instructor_bio: e.target.value})}
                    rows={4}
                    placeholder="Brief biography of the course instructor..."
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowEditCourseDialog(false);
                    setEditingCourse(null);
                    setCourseIconFile(null);
                    setInstructorPhotoFile(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  data-testid="save-edit-course-btn"
                  onClick={updateCourse}
                  className="flex-1 text-white border-0"
                  style={{ background: '#102862' }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Course Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Course</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{courseToDelete?.title}"? This will permanently delete the course and all its associated sources, assessments, surveys, and student progress. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteDialog(false);
              setCourseToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteCourse}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Source Confirmation Dialog */}
      <AlertDialog open={showDeleteSourceDialog} onOpenChange={setShowDeleteSourceDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Source</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{sourceToDelete?.title}"? This will permanently delete the source and all its associated embeddings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteSourceDialog(false);
              setSourceToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteSource}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Source
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Organize Content Dialog */}
      <Dialog open={showOrganizeDialog} onOpenChange={setShowOrganizeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle>Organize Lessons - {selectedCourse?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Create New Lesson */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Create New Lesson</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter full lesson title (e.g., Introduction to Oral Surgery)"
                  value={newLessonTitle}
                  onChange={(e) => setNewLessonTitle(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && createNewLesson()}
                />
                <Button onClick={createNewLesson} disabled={!newLessonTitle.trim()}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Lesson
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Tip: You control the title completely. Add your own numbering if desired (e.g., "1. Introduction" or "Lesson One: Basics")
              </p>
            </Card>

            {/* Lessons List with Drag and Drop */}
            <Card className="p-4">
              <h3 className="font-semibold mb-3">Course Lessons ({sources.length})</h3>
              <p className="text-sm text-gray-600 mb-4">
                Drag lessons to reorder them. Click Edit to modify content or Delete to remove.
              </p>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;

                  // Find the active and over lesson indices
                  const sortedSources = [...sources].sort((a, b) => a.order - b.order);
                  const oldIndex = sortedSources.findIndex(l => l.id === active.id);
                  const newIndex = sortedSources.findIndex(l => l.id === over.id);
                  
                  if (oldIndex === -1 || newIndex === -1) return;
                  
                  // Reorder lessons
                  const newSources = arrayMove(sortedSources, oldIndex, newIndex);
                  
                  // Update local state immediately
                  const updatedSources = newSources.map((lesson, index) => ({
                    ...lesson,
                    order: index
                  }));
                  setSources(updatedSources);
                  
                  // Persist to backend
                  reorderAllLessons(updatedSources);
                }}
              >
                <SortableContext
                  items={sources.map(s => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {[...sources].sort((a, b) => a.order - b.order).map((lesson) => (
                      <SortableLesson key={lesson.id} lesson={lesson} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {sources.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No lessons yet. Upload source materials and use AI to generate lessons, or create them manually.
                </p>
              )}
            </Card>

            {/* Source library removed - now in Sources dialog */}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Organize Item Dialog - restoring proper structure */}
      <div style={{display: 'none'}}>
              <h3 className="font-semibold mb-2">📚 Source Library</h3>
              <p className="text-xs text-gray-600 mb-4">
                Upload reference materials below. They won't appear to students until you add them as lessons above.
              </p>
              
              {/* Upload new sources */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium">Upload Video</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Title"
                      value={newVideoTitle}
                      onChange={(e) => setNewVideoTitle(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="YouTube URL"
                      value={newVideoUrl}
                      onChange={(e) => setNewVideoUrl(e.target.value)}
                      className="text-sm"
                    />
                    <Button size="sm" onClick={uploadVideoSource} disabled={!newVideoTitle.trim() || !newVideoUrl.trim()}>
                      Upload
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium">Upload Audio</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Title"
                      value={newAudioTitle}
                      onChange={(e) => setNewAudioTitle(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Audio URL"
                      value={newAudioUrl}
                      onChange={(e) => setNewAudioUrl(e.target.value)}
                      className="text-sm"
                    />
                    <Button size="sm" onClick={uploadAudioSource} disabled={!newAudioTitle.trim() || !newAudioUrl.trim()}>
                      Upload
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium">Upload PDF</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Title"
                      value={newPdfTitle}
                      onChange={(e) => setNewPdfTitle(e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setNewPdfFile(e.target.files[0])}
                      className="text-sm"
                    />
                    <Button size="sm" onClick={uploadPdfSource} disabled={!newPdfTitle.trim() || !newPdfFile}>
                      Upload
                    </Button>
                  </div>
                </div>
              </div>
      </div>

      {/* Edit Organize Item Dialog */}
      <Dialog open={!!editingOrganizeItem} onOpenChange={() => setEditingOrganizeItem(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle>
              Edit {editingOrganizeItem?.type === 'section' ? 'Section' : editingOrganizeItem?.type === 'module' ? 'Module' : 'Lesson'}
            </DialogTitle>
          </DialogHeader>
          
          {editingOrganizeItem && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <Input
                  value={editingOrganizeItem.title}
                  onChange={(e) => setEditingOrganizeItem({...editingOrganizeItem, title: e.target.value})}
                  placeholder="Enter title..."
                />
              </div>
              
              {editingOrganizeItem.type === 'lesson' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Description / Transcript</label>
                    <div className="space-y-2">
                      <div className="flex items-center gap-1 p-2 bg-gray-50 rounded-md border">
                        <button
                          type="button"
                          onClick={() => {
                            const textarea = document.getElementById('lesson-content-textarea');
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const text = editingOrganizeItem.content || '';
                            const selectedText = text.substring(start, end);
                            const newText = text.substring(0, start) + `**${selectedText}**` + text.substring(end);
                            setEditingOrganizeItem({...editingOrganizeItem, content: newText});
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Bold"
                        >
                          <Bold className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const textarea = document.getElementById('lesson-content-textarea');
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const text = editingOrganizeItem.content || '';
                            const selectedText = text.substring(start, end);
                            const newText = text.substring(0, start) + `*${selectedText}*` + text.substring(end);
                            setEditingOrganizeItem({...editingOrganizeItem, content: newText});
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Italic"
                        >
                          <Italic className="w-4 h-4" />
                        </button>
                        <div className="w-px h-6 bg-gray-300 mx-1"></div>
                        <button
                          type="button"
                          onClick={() => {
                            const textarea = document.getElementById('lesson-content-textarea');
                            const start = textarea.selectionStart;
                            const text = editingOrganizeItem.content || '';
                            const newText = text.substring(0, start) + '\n- ' + text.substring(start);
                            setEditingOrganizeItem({...editingOrganizeItem, content: newText});
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Bullet List"
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const textarea = document.getElementById('lesson-content-textarea');
                            const start = textarea.selectionStart;
                            const text = editingOrganizeItem.content || '';
                            const newText = text.substring(0, start) + '\n1. ' + text.substring(start);
                            setEditingOrganizeItem({...editingOrganizeItem, content: newText});
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Numbered List"
                        >
                          <ListOrdered className="w-4 h-4" />
                        </button>
                        <div className="w-px h-6 bg-gray-300 mx-1"></div>
                        <button
                          type="button"
                          onClick={() => {
                            const textarea = document.getElementById('lesson-content-textarea');
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const text = editingOrganizeItem.content || '';
                            const selectedText = text.substring(start, end) || 'link text';
                            const newText = text.substring(0, start) + `[${selectedText}](https://example.com)` + text.substring(end);
                            setEditingOrganizeItem({...editingOrganizeItem, content: newText});
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Add Link"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <textarea
                        id="lesson-content-textarea"
                        value={editingOrganizeItem.content || ''}
                        onChange={(e) => setEditingOrganizeItem({...editingOrganizeItem, content: e.target.value})}
                        rows={15}
                        className="w-full border rounded-md p-3 font-mono text-sm"
                        placeholder="Add lesson description, transcript, or additional content..."
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Use Markdown formatting: **bold**, *italic*, - bullets, 1. numbered lists, [link](url)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">YouTube Video ID or URL</label>
                    <Input
                      value={editingOrganizeItem.video_url || ''}
                      onChange={(e) => {
                        let value = e.target.value.trim();
                        // If it's just an ID (11 chars, no special chars), convert to full URL
                        if (value && /^[a-zA-Z0-9_-]{11}$/.test(value)) {
                          value = `https://www.youtube.com/watch?v=${value}`;
                        }
                        // If it's a youtu.be short link, convert to full URL
                        else if (value.includes('youtu.be/')) {
                          const videoId = value.split('youtu.be/')[1]?.split('?')[0]?.split('&')[0];
                          if (videoId) value = `https://www.youtube.com/watch?v=${videoId}`;
                        }
                        // If it's a full YouTube URL, extract and normalize
                        else if (value.includes('youtube.com/watch')) {
                          const urlParams = new URLSearchParams(value.split('?')[1]);
                          const videoId = urlParams.get('v');
                          if (videoId) value = `https://www.youtube.com/watch?v=${videoId}`;
                        }
                        setEditingOrganizeItem({...editingOrganizeItem, video_url: value});
                      }}
                      placeholder="dQw4w9WgXcQ or https://youtube.com/watch?v=dQw4w9WgXcQ"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Paste YouTube video ID (e.g., dQw4w9WgXcQ) or full YouTube URL
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Audio URL (direct link to MP3, etc.)</label>
                    <Input
                      value={editingOrganizeItem.audio_url || ''}
                      onChange={(e) => setEditingOrganizeItem({...editingOrganizeItem, audio_url: e.target.value})}
                      placeholder="https://example.com/audio.mp3"
                    />
                  </div>
                  
                  {/* Lesson Image Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Lesson Image/Slide (Optional)</label>
                    <p className="text-xs text-gray-500 mb-2">Upload a photo or slide to display at the top of this lesson</p>
                    {editingOrganizeItem.image_url && (
                      <div className="mb-2">
                        <img 
                          src={`${API.replace('/api', '')}${editingOrganizeItem.image_url}`}
                          alt="Lesson preview" 
                          className="max-w-xs rounded border"
                          style={{ maxHeight: '150px' }}
                        />
                        <p className="text-xs text-gray-500 mt-1">Current image - upload a new one to replace it</p>
                      </div>
                    )}
                    {editingOrganizeItem.image_file && (
                      <div className="mb-2 p-2 bg-green-50 rounded border border-green-200">
                        <p className="text-xs text-green-700 font-medium">✓ New image selected: {editingOrganizeItem.image_file.name}</p>
                        <p className="text-xs text-green-600">This will replace the current image when you save</p>
                      </div>
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setEditingOrganizeItem({...editingOrganizeItem, image_file: file});
                        }
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">Description (Markdown supported)</label>
                    <textarea
                      value={editingOrganizeItem.description || ''}
                      onChange={(e) => setEditingOrganizeItem({...editingOrganizeItem, description: e.target.value})}
                      rows={8}
                      className="w-full border rounded-md p-3 font-mono text-sm"
                      placeholder="Enter description..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">YouTube Video ID or URL</label>
                    <Input
                      value={editingOrganizeItem.video_url || ''}
                      onChange={(e) => {
                        let value = e.target.value.trim();
                        // If it's just an ID (11 chars, no special chars), convert to full URL
                        if (value && /^[a-zA-Z0-9_-]{11}$/.test(value)) {
                          value = `https://www.youtube.com/watch?v=${value}`;
                        }
                        // If it's a youtu.be short link, convert to full URL
                        else if (value.includes('youtu.be/')) {
                          const videoId = value.split('youtu.be/')[1]?.split('?')[0]?.split('&')[0];
                          if (videoId) value = `https://www.youtube.com/watch?v=${videoId}`;
                        }
                        // If it's a full YouTube URL, extract and normalize
                        else if (value.includes('youtube.com/watch')) {
                          const urlParams = new URLSearchParams(value.split('?')[1]);
                          const videoId = urlParams.get('v');
                          if (videoId) value = `https://www.youtube.com/watch?v=${videoId}`;
                        }
                        setEditingOrganizeItem({...editingOrganizeItem, video_url: value});
                      }}
                      placeholder="dQw4w9WgXcQ or https://youtube.com/watch?v=dQw4w9WgXcQ"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Paste YouTube video ID (e.g., dQw4w9WgXcQ) or full YouTube URL
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Audio URL (direct link to MP3, etc.)</label>
                    <Input
                      value={editingOrganizeItem.audio_url || ''}
                      onChange={(e) => setEditingOrganizeItem({...editingOrganizeItem, audio_url: e.target.value})}
                      placeholder="https://example.com/audio.mp3"
                    />
                  </div>
                </>
              )}
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingOrganizeItem(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const formData = new FormData();
                      formData.append('title', editingOrganizeItem.title);
                      
                      if (editingOrganizeItem.type === 'lesson') {
                        formData.append('content', editingOrganizeItem.content || '');
                      } else {
                        formData.append('description', editingOrganizeItem.description || '');
                      }
                      
                      // Add video and audio URLs for all types
                      formData.append('video_url', editingOrganizeItem.video_url || '');
                      formData.append('audio_url', editingOrganizeItem.audio_url || '');
                      
                      // Add image file if selected (lessons only)
                      if (editingOrganizeItem.type === 'lesson' && editingOrganizeItem.image_file) {
                        formData.append('image', editingOrganizeItem.image_file);
                      }
                      
                      let endpoint = '';
                      if (editingOrganizeItem.type === 'section') {
                        endpoint = `${API}/admin/courses/${selectedCourse.id}/sections/${editingOrganizeItem.id}`;
                      } else if (editingOrganizeItem.type === 'module') {
                        endpoint = `${API}/admin/courses/${selectedCourse.id}/modules/${editingOrganizeItem.id}`;
                      } else {
                        endpoint = `${API}/admin/sources/${editingOrganizeItem.id}`;
                      }
                      
                      const response = await axios.put(endpoint, formData, { headers });
                      
                      console.log('Update response:', response);
                      toast.success(`${editingOrganizeItem.type.charAt(0).toUpperCase() + editingOrganizeItem.type.slice(1)} updated successfully!`);
                      setEditingOrganizeItem(null);
                      
                      // Refresh data
                      if (selectedCourse) {
                        await fetchSectionsAndModules(selectedCourse.id);
                        await fetchSources(selectedCourse.id);
                      }
                    } catch (error) {
                      console.error('Update error details:', error);
                      console.error('Error response:', error.response);
                      toast.error(`Error updating item: ${error.response?.data?.detail || error.message}`);
                    }
                  }}
                  style={{ background: '#102862', color: 'white' }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Content Preview Dialog */}
      <Dialog open={showContentPreview} onOpenChange={setShowContentPreview}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Course Content - {selectedCourse?.title}</span>
              <Button
                size="sm"
                onClick={() => {
                  setShowContentPreview(false);
                  navigate(`/admin/course/${selectedCourse?.id}/editor`);
                }}
                style={{ background: '#102862', color: 'white' }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Full Editor
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto mt-4">
            {contentSections.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No course content available yet</p>
                <p className="text-sm text-gray-500 mb-4">Upload sources and click "AI Generate" to create structured content</p>
                <Button
                  onClick={() => {
                    setShowContentPreview(false);
                  }}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {contentSections.map((section, sIdx) => (
                  <Card key={section.id} className="p-4" style={{ borderColor: '#DADEEO' }}>
                    {/* Section Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-2 flex-1">
                        <button
                          onClick={() => setExpandedContentSections({
                            ...expandedContentSections,
                            [section.id]: !expandedContentSections[section.id]
                          })}
                          className="mt-1"
                        >
                          {expandedContentSections[section.id] ? (
                            <ChevronDown className="w-5 h-5" style={{ color: '#102862' }} />
                          ) : (
                            <ChevronRight className="w-5 h-5" style={{ color: '#102862' }} />
                          )}
                        </button>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg" style={{ color: '#102862' }}>
                            {section.title}
                          </h3>
                          {section.description && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {section.description.replace(/[#*]/g, '').substring(0, 150)}...
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {section.modules?.length || 0} modules
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Modules */}
                    {expandedContentSections[section.id] && (
                      <div className="ml-7 mt-3 space-y-3">
                        {section.modules?.map((module, mIdx) => (
                          <div key={module.id} className="border-l-2 pl-4" style={{ borderColor: '#DADEEO' }}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-start gap-2 flex-1">
                                <button
                                  onClick={() => setExpandedContentModules({
                                    ...expandedContentModules,
                                    [module.id]: !expandedContentModules[module.id]
                                  })}
                                  className="mt-0.5"
                                >
                                  {expandedContentModules[module.id] ? (
                                    <ChevronDown className="w-4 h-4" style={{ color: '#092036' }} />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" style={{ color: '#092036' }} />
                                  )}
                                </button>
                                <div className="flex-1">
                                  <h4 className="font-semibold" style={{ color: '#092036' }}>
                                    {module.title}
                                  </h4>
                                  {module.description && (
                                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                      {module.description.replace(/[#*]/g, '').substring(0, 120)}...
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">
                                    {module.lessons?.length || 0} lessons
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Lessons */}
                            {expandedContentModules[module.id] && (
                              <div className="ml-6 mt-2 space-y-2">
                                {module.lessons?.map((lesson, lIdx) => (
                                  <div key={lesson.id} className="bg-gray-50 p-3 rounded">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-900">
                                          {lesson.title}
                                        </p>
                                        {lesson.content && (
                                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                            {lesson.content.replace(/[#*]/g, '').substring(0, 150)}...
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
                
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    💡 <strong>Tip:</strong> For full editing capabilities (add, edit, delete, reorder), click "Open Full Editor" above.
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Featured Course Dialog */}
      <Dialog open={showFeaturedDialog} onOpenChange={setShowFeaturedDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle>Feature Course: {selectedCourse?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Featured Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_featured"
                checked={featuredForm.is_featured}
                onChange={(e) => setFeaturedForm({ ...featuredForm, is_featured: e.target.checked })}
                className="w-5 h-5"
              />
              <Label htmlFor="is_featured" className="text-base font-semibold">
                ⭐ Feature this course on landing page
              </Label>
            </div>

            {featuredForm.is_featured && (
              <>
                {/* Featured Order */}
                <div>
                  <Label htmlFor="featured_order">Featured Position (1-3, lower = higher priority)</Label>
                  <Input
                    id="featured_order"
                    type="number"
                    min="1"
                    max="3"
                    value={featuredForm.featured_order}
                    onChange={(e) => setFeaturedForm({ ...featuredForm, featured_order: parseInt(e.target.value) })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Position 1 appears first, up to 3 courses can be featured</p>
                </div>

                {/* Learning Outcomes */}
                <div>
                  <Label className="text-base">Learning Outcomes (3-5 recommended)</Label>
                  <p className="text-xs text-gray-500 mb-2">What will students learn from this course?</p>
                  
                  <div className="space-y-2">
                    {featuredForm.learning_outcomes.map((outcome, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={outcome}
                          onChange={(e) => updateLearningOutcome(index, e.target.value)}
                          placeholder={`Learning outcome ${index + 1}`}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeLearningOutcome(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {featuredForm.learning_outcomes.length < 5 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addLearningOutcome}
                      className="mt-2"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Learning Outcome
                    </Button>
                  )}
                </div>

                {/* Requirements */}
                <div>
                  <Label htmlFor="requirements">Course Requirements / Prerequisites</Label>
                  <Textarea
                    id="requirements"
                    value={featuredForm.requirements}
                    onChange={(e) => setFeaturedForm({ ...featuredForm, requirements: e.target.value })}
                    placeholder="e.g., Basic understanding of oral surgery, current dental license..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowFeaturedDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={saveFeaturedSettings}
                style={{ background: '#102862' }}
                className="text-white"
              >
                Save Featured Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle>Manage Categories: {selectedCourse?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Create New Category */}
            <Card className="p-4 bg-gray-50">
              <h3 className="text-sm font-semibold mb-3">Create New Category</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Category name (e.g., Surgical Techniques)"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && createCategory()}
                />
                <Button onClick={createCategory} style={{ background: '#10B981', color: 'white' }}>
                  Create
                </Button>
              </div>
            </Card>

            {/* Existing Categories */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Select Categories for This Course</h3>
              {categories.length === 0 ? (
                <p className="text-sm text-gray-500">No categories yet. Create one above!</p>
              ) : (
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div key={category.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                      <label className="flex items-center gap-3 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={courseForm.categories?.includes(category.id)}
                          onChange={() => toggleCourseCategory(category.id)}
                          className="w-4 h-4"
                        />
                        <span className="font-medium">{category.name}</span>
                      </label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCategory(category.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Delete
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={saveCourseCategories}
                style={{ background: '#102862' }}
                className="text-white"
              >
                Save Categories
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Management Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="max-w-2xl light-form-card">
          <DialogHeader>
            <DialogTitle>Manage Admin Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Add New Admin */}
            <div className="p-4 border rounded-lg" style={{ borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Add New Admin
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    placeholder="Full name"
                    value={newAdminForm.name}
                    onChange={(e) => setNewAdminForm({...newAdminForm, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newAdminForm.email}
                    onChange={(e) => setNewAdminForm({...newAdminForm, email: e.target.value})}
                  />
                </div>
              </div>
              <Button
                onClick={addAdmin}
                className="mt-3 text-white"
                style={{ background: '#102862' }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Admin
              </Button>
              <p className="text-xs text-gray-500 mt-2">
                New admins will need to use "Forgot Password" to set their password.
              </p>
            </div>

            {/* Current Admins List */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Current Admins ({adminUsers.length})
              </h3>
              <div className="space-y-2">
                {adminUsers.map((admin) => (
                  <div
                    key={admin.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                    style={{ borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}
                  >
                    <div>
                      <p className="font-medium">{admin.name}</p>
                      <p className="text-sm text-gray-500">{admin.email}</p>
                    </div>
                    {admin.id !== user?.id ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAdminToDelete(admin)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded">You</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Admin Confirmation */}
      <AlertDialog open={!!adminToDelete} onOpenChange={() => setAdminToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{adminToDelete?.name}</strong> ({adminToDelete?.email}) as an admin? 
              This will delete their admin account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeAdmin(adminToDelete?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}